import { FormActions } from "../../components/common/FormActions";
import { TTableColumn } from "../../types/common";
import {
  beautify,
  createCloseAction,
  createSelectItem,
  createViewEdit,
  displayDocuments,
  getResultOptions,
  itemExists,
  numberFormat,
  removeUnnecessaryFields,
  returnObject,
  showObjectItems,
  useGetUserPermissions,
} from "../../utils/helpers";
import {
  APPROVED,
  CAN_ADD_LOAN_USER,
  CAN_MAKE_PAYMENTS,
  CAN_SPECIAL_EDIT,
  DEFERRED,
  DISBURSED,
  RECOMMENDED_FOR_APPROVAL,
  REJECTED,
  SUBMITTED,
  CAN_APPROVE_APPLICATION,
  CAN_RECOMMEND_APPLICATION,
  RESTRUCTURED_APPLICANT,
} from "../../utils/constants";
import { sourceApis } from "../handlers/useApiHandler";
import {
  API_INVENTORY_ALL,
  API_LOAN_APPLICATIONS,
  API_PRODUCTS,
} from "../../utils/apis";
import { useEffect, useState } from "react";
import useGet from "../common/useGet";
import { Link } from "react-router-dom";
import { Button } from "@mui/material";
import { PaymentStatusTag } from "../../components/common/registerUtils";
import { ArrowRedoOutline } from "@raresail/react-ionicons";
import DetailsCard from "../../components/common/DetailsCards";

// export const useLoanApplication = ({ status }: any) => {
export const useLoanApplication = ({ status }: { status?: string } = {}) => {
  const permissions = useGetUserPermissions();
  const normalizedStatus = status || "";
  const canEdit = itemExists(permissions, [
    CAN_ADD_LOAN_USER,
    CAN_SPECIAL_EDIT,
  ]);
  const canMakePayments = itemExists(permissions, [CAN_MAKE_PAYMENTS]);
  const [options, setOptions] = useState({ interests: [] });

  const { loadData: getInterests, data: interestData } = useGet();

  useEffect(() => {
    getInterests({
      api: "configurables/interest-configurations/",
      params: {},
    });
  }, []);

  useEffect(() => {
    setOptions({
      ...options,
      interests: getResultOptions(interestData, "purpose", "purpose"),
    });
  }, [interestData]);

  const tableColumns = (): TTableColumn[] => [
    {
      id: "application_number",
      header: "Application Number",
      filter: {
        type: "text",
      },
    },
    {
      id: "applicant",
      header: "Applicant",
      filter: {
        type: "text",
      },
    },
    {
      id: "application_type",
      header: "Application Type",
      filter: {
        type: "select",
        options: [
          { id: "New Applicant", name: "New Applicant" },
          { id: "Existing Applicant", name: "Existing Applicant" },
          { id: "Migrated Applicant", name: "Migrated Applicant" },
          { id: "Restructured Applicant", name: "Restructured Applicant" },
        ],
      },
    },
    ...returnObject(["disbursed", "closed"].includes(normalizedStatus), [
      {
        id: "number_plate",
        header: "Number Plate",
        filter: {
          type: "text",
        },
      },
      {
        id: "disbursement_date",
        header: "Disbursement Date",
        filter: {
          type: "date",
        },
      },
    ]),
    ...returnObject(
      ["submitted", "approved", "disbursed"].includes(normalizedStatus),
      [
        {
          id: "payment_status",
          header: "Payment Status",
          filter: {
            type: "select",
            options: [
              { id: "paid", name: "Completed" },
              { id: "partially_paid", name: "Running" },
              { id: "not_paid", name: "Not Paid" },
            ],
          },
        },
      ],
    ),

    ...returnObject(canMakePayments && [DISBURSED].includes(normalizedStatus), [
      { id: "total_amount_payable", header: "Total Amount Payable" },
    ]),
    { id: "invoices", header: "Invoices" },
    ...returnObject(
      canMakePayments &&
        ["submitted", "approved", "disbursed"].includes(normalizedStatus),
      [
        {
          id: "make_payment",
          header: "Make Payment",
        },
      ],
    ),
    { id: "actions", header: "Actions" },
  ];

  const tableData = (data: any) =>
    data.map((item: any) => ({
      ...item,
      application_number: (
        <Link
          className="link-with-icon"
          to={`/loan_application/view/${item?.id}`}
        >
          {item?.application_number}
        </Link>
      ),
      total_amount_payable: numberFormat(item?.total_amount_payable),
      applicant: `${item?.first_name} ${item?.other_name || ""} ${item?.last_name}`,
      payment_status: <PaymentStatusTag status={item?.payment_status} />,
      number_plate: (
        <div style={{ whiteSpace: "nowrap" }}>{item?.number_plate}</div>
      ),
      invoices: (
        <Button
          variant="outlined"
          color="primary"
          size="small"
          style={{ cursor: "pointer" }}
        >
          <Link
            className="link-with-icon"
            to={`/register/invoices?loan_application=${item?.id}`}
          >
            Invoices
            <ArrowRedoOutline />
          </Link>
        </Button>
      ),
      make_payment:
        item?.payment_status !== "paid" ? (
          <Button
            variant="outlined"
            color="success"
            size="small"
            style={{ cursor: "pointer" }}
          >
            <Link
              className="link-with-icon"
              to={`/make_payment/create/?loan_application=${item?.id}`}
              style={{
                whiteSpace: "nowrap",
                color: "var(--success-color) !important",
              }}
            >
              Make Payment
              <ArrowRedoOutline />
            </Link>
          </Button>
        ) : (
          <></>
        ),
      actions: (
        <FormActions
          actions={[
            ...createViewEdit("/loan_application", item?.id, canEdit),
            ...returnObject(item?.status === "disbursed" && canEdit, [
              createCloseAction(
                `/loan-applications/${item?.id}/close-application/`,
                item?.application_number,
              ),
            ]),
          ]}
        />
      ),
    }));

  const viewData = (item: any) => {
    const getGarantors = () => {
      return item?.guarantors?.length > 0
        ? item.guarantors.map((guarantor, key) => ({
            title: `Guarantor Details ${key + 1}`,
            fields: {
              first_name: guarantor.first_name,
              last_name: guarantor.last_name,
              occupation: guarantor.profession,
              phone_number: guarantor.phone_number,
              guarantor_id: guarantor.id,
              physical_address: guarantor.physical_address,
              ...displayDocuments(
                guarantor?.loan_application_guarantor_attachments,
              ),
            },
          }))
        : [];
    };

    const docsList: any = displayDocuments(item?.loan_application_attachments);

    return {
      sections: [
        {
          title: "Personal Information",
          fields: {
            application_number: item?.application_number,
            application_type: item?.application_type,
            applicant_name: `${item?.first_name} ${item?.other_name || ""} ${item?.last_name}`,
            NIN: item?.nin,
            tin: item?.tin,
            phone_number: item?.phone_number,
            alternative_phone_number: item?.alternative_phone_number,
            email: item?.email,
            ...returnObject(item?.application_type === "Existing Applicant", {
              number_plate: item?.number_plate,
            }),
            residential_address: item?.residential_address,
            status: beautify(item?.status),
            payment_status: <PaymentStatusTag status={item?.payment_status} />,
            invoices: (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                style={{ cursor: "pointer" }}
              >
                <Link
                  className="link-with-icon"
                  to={`/register/invoices?loan_application=${item?.id}`}
                >
                  Invoices
                  <ArrowRedoOutline />
                </Link>
              </Button>
            ),
          },
        },
        {
          title: "Location Details",
          fields: {
            district: item?.district_name,
            county: item?.county_name,
            sub_county: item?.sub_county_name,
            village: item?.village_name,
            latitude: item?.latitude,
            longitude: item?.longitude,
          },
        },

        {
          title: "Employment Details",
          fields: {
            category: item?.category,
            ...returnObject(item?.category === "Stage", {
              stage_name: item?.stage_name,
              stage_id: item?.stage_id,
              year_joined: item?.year_joined,
            }),
            ...returnObject(item?.category === "Work", {
              work_id: item?.work_id,
            }),
            recommendation_letter: item?.recommendation_letter,
          },
        },
        {
          title: "LC1 Chairperson’s Details",
          fields: {
            first_name: item?.local_council_chairperson?.first_name,
            last_name: item?.local_council_chairperson?.last_name,
            other_name: item?.local_council_chairperson?.other_name,
            phone_number: item?.local_council_chairperson?.phone_number,
            address: item?.local_council_chairperson?.address,
            letter: item?.local_council_chairperson?.letter,
            ...displayDocuments(
              item?.local_council_chairperson
                ?.loan_application_local_council_chairperson_attachments,
            ),
          },
        },
        {
          title: "Stage Chairperson’s Details",
          visible: item?.category === "Stage",
          fields: {
            first_name: item?.stage_chairperson?.first_name,
            last_name: item?.stage_chairperson?.last_name,
            other_name: item?.stage_chairperson?.other_name,
            phone_number: item?.stage_chairperson?.phone_number,
            address: item?.stage_chairperson?.physical_address,
            ...displayDocuments(
              item?.stage_chairperson
                ?.loan_application_stage_chairperson_attachments,
            ),
          },
        },
        ...getGarantors(),
        {
          title: "Loan Particulars",
          fields: {
            loan_type: item?.loan_type,
            loan_validity_period: `${item?.loan_validity_period_years} years`,
            number_of_months: item?.loan_validity_period_months,
            repayment_period: item?.repayment_period,
            amortization_method: item?.amortization_method,
            ...returnObject([DISBURSED].includes(item?.status), {
              amount: numberFormat(item?.amount),
              interest_percent: item?.interest_percent,
              interest: numberFormat(item?.interest),
              total_loan_value: numberFormat(item?.total_loan_value),
            }),
          },
        },
        {
          title: "Field Officer Details",
          fields: {
            first_name: item?.field_officer_details?.first_name,
            last_name: item?.field_officer_details?.last_name,
            gender: item?.field_officer_details?.gender,
            phone_number: item?.field_officer_details?.phone_number,
            email: item?.field_officer_details?.email,
          },
        },
        {
          title: "Supporting Documents",
          fields: {
            ...docsList,
            ...returnObject(docsList.agreement_, {
              agreement_: (
                <div style={{ display: "flex", gap: 10 }}>
                  {docsList.agreement_}
                  {/* {useGetUserPermissions().includes(
                    CAN_APPROVE_APPLICATION,
                  ) && <RegenerateDocuments item={item} />} */}
                </div>
              ),
            }),
          },
        },
        {
          title: " Product to be issued",
          visible:
            item?.status !== DISBURSED &&
            item?.application_type === RESTRUCTURED_APPLICANT,
          fields: {
            ...showObjectItems(item?.previous_loan_application_product, [
              "id",
              "inventory",
              "loan_application",
              "declaration",
              "created_at",
              "updated_at",
              "deleted",
              "purchase_order_inventory",
            ]),
          },
        },
        {
          title: "Issued Product",
          visible: item?.status === DISBURSED,
          fields: {
            ...showObjectItems(item?.product, [
              "id",
              "inventory",
              "loan_application",
              "declaration",
              "created_at",
              "updated_at",
              "deleted",
              "purchase_order_inventory",
            ]),
          },
        },
      ],
    };
  };

  return { tableColumns, tableData, viewData };
};

export function useLoanApplicationActions({ form, data, updateFormValue }) {
  const [selectedProduct, setSelectedProduct] = useState<any>({});

  const { loadData: getProducts, data: products } = useGet();
  const { loadData: getInventories, data: inventories } = useGet();

  const getProductOptions = (data: any) => {
    return data?.results?.length
      ? data.results.map((item: any) => ({
          id: item.id,
          name: `${item?.name || ""}, ${item?.number_plate || ""}, ${item?.registration_number || ""}`,
        }))
      : [];
  };

  useEffect(() => {
    if ([APPROVED, DISBURSED].includes(data?.status)) {
      getInventories({
        api: API_INVENTORY_ALL,
        params: {
          category_name: "loan",
        },
      });
    }
  }, []);

  useEffect(() => {
    if (form?.inventory != null) {
      getProducts({
        api: API_PRODUCTS,
        params: {
          inventory: form.inventory,
          status: "in_stock",
        },
      });
    }
  }, [form.inventory]);

  useEffect(() => {
    if (form?.product) {
      const product = products?.results?.find(
        (item: any) => item.id === form?.product,
      );
      delete product.inventory;
      delete product.inventory_name;
      delete product.purchase_order;
      delete product.purchase_order_inventory;
      setSelectedProduct(product);
      updateFormValue("amount", product?.unit_price);
    }
  }, [form?.product]);

  // action select options
  let actionsList = [RECOMMENDED_FOR_APPROVAL, APPROVED, DEFERRED, REJECTED];

  if (data?.status === "submitted") {
    actionsList = [RECOMMENDED_FOR_APPROVAL, DEFERRED];
  }
  if (data?.status === "recommended_for_approval") {
    actionsList = [APPROVED, REJECTED];
  }
  const permissions = useGetUserPermissions();
  const can_recommended_for_approval =
    itemExists(permissions, [CAN_RECOMMEND_APPLICATION]) &&
    data?.status === SUBMITTED;

  const can_approve =
    itemExists(permissions, [CAN_APPROVE_APPLICATION]) &&
    [RECOMMENDED_FOR_APPROVAL, APPROVED, DISBURSED].includes(data?.status);

  const actions = [...actionsList.map((action) => createSelectItem(action))];
  // consturct action form fields
  const recommendationFields = [
    {
      field_name: "comment",
      label: "comment",
      type: "textarea",
      props: {
        wide: true,
      },
    },
  ];

  const approvalFields = [
    {
      field_name: "comment",
      label: "Comment",
      type: "textarea",
      props: {
        required: true,
      },
      visible: data?.status === REJECTED,
    },
  ];
  const issueAgreementFields = (reissue: boolean) => [
    {
      field_name: "inventory",
      label: reissue ? "Change Inventory" : "Inventory Categories",
      type: "select",
      props: { required: true },
      options_name: "inventories",
    },
    {
      field_name: "product",
      label: reissue ? "Change Product" : "Issue Product",
      type: "select",
      props: { required: true },
      options_name: "products",
      visible: form?.inventory != null,
    },
    {
      field_name: "amount",
      label: "Amount",
      type: "number",
      props: { required: true },
      visible: form?.product != null,
    },
    {
      field_name: "product_details",
      label: reissue ? "New Product Details" : "Product Details",
      type: "component",
      props: { required: true, newLine: true, wide: true },
      component: (
        <DetailsCard
          viewData={{
            brand: selectedProduct?.name,
            chassis_number: selectedProduct?.registration_number,
            ...removeUnnecessaryFields(selectedProduct, [
              "name",
              "registration_number",
              "description",
            ]),
            unit_price: numberFormat(selectedProduct?.unit_price),
          }}
        />
      ),
      visible: form?.product != null,
    },
  ];

  const restructuredApplicantFields = [
    {
      field_name: "issue_product",
      label: "Re-issue Product",
      type: "select",
      props: { required: true, newLine: true },
      options_data: [{ id: true, name: "Yes" }],
    },
  ];

  let fields = [
    {
      field_name: "status",
      type: "select",
      props: { required: true },
      options_data: actions,
      visible: data?.status !== APPROVED && data?.status !== DISBURSED,
    },
    ...returnObject(data?.status === SUBMITTED, recommendationFields),
    ...returnObject(data?.status === RECOMMENDED_FOR_APPROVAL, approvalFields),
    ...returnObject(
      data?.status === APPROVED,
      data?.application_type === RESTRUCTURED_APPLICANT
        ? restructuredApplicantFields
        : issueAgreementFields(false),
    ),
    ...returnObject(data?.status === DISBURSED, issueAgreementFields(true)),
  ];

  if (!can_recommended_for_approval && !can_approve) {
    fields = [];
  }

  const useGetActionOptions = () => {
    return {
      products: getProductOptions(products),
      inventories: getResultOptions(inventories, "name", "id"),
    };
  };
  const options = useGetActionOptions();

  // for submitting actions
  function onSubmit(payload: any, id: any) {
    if (id) {
      let api = sourceApis["loan_application"];
      if (data.status === APPROVED) {
        api = `${API_LOAN_APPLICATIONS}${id}/issue-agreement/`;
        id = null;
      }
      if (data.status === DISBURSED) {
        api = `${API_LOAN_APPLICATIONS}${id}/change-product/`;
        id = null;
      }

      if (!api) return;
      return {
        id,
        api,
        params: { ...payload },
      };
    }
  }
  const enableSubmit = Object.keys(form).length > 0;
  const finalStates = [DEFERRED, REJECTED];

  return {
    fields,
    onSubmit,
    enableSubmit,
    options,
    finalStates,
  };
}
